import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { DataTable, type DataTableColumn } from '@/components/data-display/data-table'
import { useFeedback } from '@/components/feedback/feedback-context'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { Select } from '@/components/forms/select'
import { Switch } from '@/components/forms/switch'
import { PageContentBoundary } from '@/components/layout/page-content-boundary'
import { PageHeader } from '@/components/layout/page-header'
import { HelpTooltip } from '@/components/overlays/help-tooltip'
import { Button } from '@/components/primitives/button'
import { formatDateTime } from '@/features/admin/shared/format'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { cn } from '@/lib/cn'
import { readStoredPortalAccount } from '@/lib/auth/session-storage'
import { ApiError } from '@/lib/api/http-client'

import {
  getAuthzSyncConfig,
  getOrganizationSyncSettings,
  listOrganizationSyncRuns,
  listOrganizationSyncTargets,
  runAuthzSync,
  triggerOrganizationSync,
  updateAuthzSyncConfig,
  updateOrganizationSyncSettings,
  type AdminAuthzSyncConfigPayload,
  type AdminAuthzSyncResult,
  type AdminOrganizationSyncRun,
  type AdminOrganizationSyncSettingsPayload,
} from './api'
import {
  invalidateOrganizationDataQueries,
  organizationSyncRunsQueryKey,
  organizationSyncSettingsQueryKey,
  organizationSyncStatusQueryKey,
  organizationSyncTargetsQueryKey,
  useOrganizationSyncStatus,
} from './sync-status'

const authzSyncConfigQueryKey = ['admin-authz-sync-config'] as const
const externalSyncPollIntervalMs = 2_000
const externalSyncIdlePollIntervalMs = 10_000
const externalSyncPollTimeoutMs = 30 * 60 * 1_000

const initialExternalForm: AdminAuthzSyncConfigPayload = {
  cron_expression: '0 0 18 * * *',
  is_enabled: false,
}

const initialSystemForm: AdminOrganizationSyncSettingsPayload = {
  cron_expression: '0 2 * * *',
  is_enabled: false,
  sync_company_ids: [],
  sync_scope: 'all',
}

type SyncSettingsTab = 'external' | 'history' | 'system'

interface ExternalRunWatch {
  baselineTime: string | null
  deadlineAt: number
}

export function AdminOrganizationSyncSettingsPage() {
  const { t } = useTranslation('adminOrganization')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const role = readStoredPortalAccount('admin').role
  const canWrite = role === 'admin' || role === 'super_admin'
  const [activeTab, setActiveTab] = useState<SyncSettingsTab>('external')
  const [externalDraft, setExternalDraft] = useState<AdminAuthzSyncConfigPayload | null>(null)
  const [externalRunWatch, setExternalRunWatch] = useState<ExternalRunWatch | null>(null)
  const [systemDraft, setSystemDraft] = useState<AdminOrganizationSyncSettingsPayload | null>(null)
  const externalRunSawRunningRef = useRef(false)
  const externalCompletionMarkerRef = useRef<string | null | undefined>(undefined)

  const authzConfigQuery = useQuery({
    queryFn: getAuthzSyncConfig,
    queryKey: authzSyncConfigQueryKey,
    refetchInterval: externalRunWatch ? externalSyncPollIntervalMs : externalSyncIdlePollIntervalMs,
  })
  const systemSettingsQuery = useQuery({
    queryFn: getOrganizationSyncSettings,
    queryKey: organizationSyncSettingsQueryKey,
  })
  const targetsQuery = useQuery({
    queryFn: listOrganizationSyncTargets,
    queryKey: organizationSyncTargetsQueryKey,
  })
  const runsQuery = useQuery({
    queryFn: () => listOrganizationSyncRuns(20),
    queryKey: organizationSyncRunsQueryKey,
  })
  const systemStatusQuery = useOrganizationSyncStatus()

  const savedExternalForm = authzConfigQuery.data
    ? {
        cron_expression: authzConfigQuery.data.cron_expression,
        is_enabled: authzConfigQuery.data.is_enabled,
      }
    : initialExternalForm
  const externalForm = externalDraft ?? savedExternalForm
  const externalDirty = externalDraft !== null
  const needsExternalInitialization = authzConfigQuery.data?.is_initialized === false
  const externalCronIsValid = hasCronFields(externalForm.cron_expression, 6)

  const savedSystemForm = systemSettingsQuery.data
    ? {
        cron_expression: systemSettingsQuery.data.cron_expression,
        is_enabled: systemSettingsQuery.data.is_enabled,
        sync_company_ids: systemSettingsQuery.data.sync_company_ids,
        sync_scope: systemSettingsQuery.data.sync_scope,
      }
    : initialSystemForm
  const rawSystemForm = systemDraft ?? savedSystemForm
  const availableTargetIds =
    targetsQuery.data === undefined ? null : new Set(targetsQuery.data.map((target) => target.id))
  const validSystemCompanyIds =
    availableTargetIds === null
      ? rawSystemForm.sync_company_ids
      : rawSystemForm.sync_company_ids.filter((id) => availableTargetIds.has(id))
  const systemForm =
    validSystemCompanyIds.length === rawSystemForm.sync_company_ids.length
      ? rawSystemForm
      : { ...rawSystemForm, sync_company_ids: validSystemCompanyIds }
  const systemDirty =
    systemDraft !== null || validSystemCompanyIds.length !== rawSystemForm.sync_company_ids.length
  const systemCronIsValid = hasCronFields(systemForm.cron_expression, 5)
  const systemScopeIsValid =
    systemForm.sync_scope === 'all' || systemForm.sync_company_ids.length > 0
  const systemFormIsValid = systemCronIsValid && systemScopeIsValid
  const systemStatus =
    systemStatusQuery.data ?? systemSettingsQuery.data?.active_lock ?? initialSystemLock
  const externalCompletionMarker =
    authzConfigQuery.data === undefined
      ? undefined
      : authzConfigQuery.data.last_sync_time &&
          (authzConfigQuery.data.last_sync_status === 'success' ||
            authzConfigQuery.data.last_sync_status === 'failed')
        ? `${authzConfigQuery.data.last_sync_status}:${authzConfigQuery.data.last_sync_time}`
        : null

  const targetNameById = useMemo(
    () => new Map((targetsQuery.data ?? []).map((target) => [target.id, target.name])),
    [targetsQuery.data],
  )

  const runColumns = useMemo<DataTableColumn<AdminOrganizationSyncRun>[]>(
    () => [
      {
        header: t('syncSettings.history.columns.status'),
        id: 'status',
        render: (run) => (
          <Badge variant={statusVariant(run.status)}>
            {t(`syncSettings.status.values.${run.status}`)}
          </Badge>
        ),
        width: 110,
      },
      {
        header: t('syncSettings.history.columns.startedAt'),
        id: 'startedAt',
        render: (run) => formatDateTime(run.started_at),
        width: 180,
      },
      {
        header: t('syncSettings.history.columns.scope'),
        id: 'scope',
        render: (run) => formatRunScope(run, targetNameById, t),
        width: 220,
      },
      {
        header: t('syncSettings.history.columns.result'),
        id: 'result',
        render: (run) =>
          t('syncSettings.history.resultCounts', {
            relations: run.relations_count,
            units: run.units_count,
          }),
        width: 210,
      },
      {
        header: t('syncSettings.history.columns.duration'),
        id: 'duration',
        render: (run) => formatDuration(run.duration_ms),
        width: 120,
      },
      {
        header: t('syncSettings.history.columns.message'),
        id: 'message',
        render: (run) => (
          <span
            className="block max-w-[320px] truncate text-[var(--color-text-muted)]"
            title={displayText(run.message)}
          >
            {displayText(run.message)}
          </span>
        ),
        width: 320,
      },
    ],
    [t, targetNameById],
  )

  const refreshExternalConfig = async () => {
    await queryClient.invalidateQueries({ queryKey: authzSyncConfigQueryKey })
  }
  const refreshSystemData = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: organizationSyncSettingsQueryKey }),
      queryClient.invalidateQueries({ queryKey: organizationSyncRunsQueryKey }),
      queryClient.invalidateQueries({ queryKey: organizationSyncStatusQueryKey }),
    ])
  }

  const saveExternalMutation = useMutation({
    mutationFn: () => updateAuthzSyncConfig(externalForm),
    onError: () =>
      showFeedback({ message: t('syncSettings.feedback.externalSaveFailed'), tone: 'error' }),
    onSuccess: async () => {
      setExternalDraft(null)
      await refreshExternalConfig()
      showFeedback({ message: t('syncSettings.feedback.externalSaved'), tone: 'success' })
    },
  })
  const runExternalMutation = useMutation({
    mutationFn: runAuthzSync,
    onError: (error) => showFeedback({ message: externalRunErrorMessage(error, t), tone: 'error' }),
    onSuccess: () => {
      externalRunSawRunningRef.current = false
      setExternalRunWatch({
        baselineTime: authzConfigQuery.data?.last_sync_time ?? null,
        deadlineAt: Date.now() + externalSyncPollTimeoutMs,
      })
      showFeedback({ message: t('syncSettings.feedback.externalSubmitted'), tone: 'info' })
      void refreshExternalConfig()
    },
  })
  const saveSystemMutation = useMutation({
    mutationFn: () => updateOrganizationSyncSettings(systemForm),
    onError: () =>
      showFeedback({ message: t('syncSettings.feedback.systemSaveFailed'), tone: 'error' }),
    onSuccess: async () => {
      setSystemDraft(null)
      await refreshSystemData()
      showFeedback({ message: t('syncSettings.feedback.systemSaved'), tone: 'success' })
    },
  })
  const runSystemMutation = useMutation({
    mutationFn: () =>
      triggerOrganizationSync(
        systemForm.sync_scope === 'selected' ? systemForm.sync_company_ids : [],
      ),
    onError: () =>
      showFeedback({ message: t('syncSettings.feedback.systemRunFailed'), tone: 'error' }),
    onSuccess: async () => {
      await Promise.all([refreshSystemData(), invalidateOrganizationDataQueries(queryClient)])
      showFeedback({ message: t('syncSettings.feedback.systemCompleted'), tone: 'success' })
    },
  })

  const updateExternalForm = (values: Partial<AdminAuthzSyncConfigPayload>) => {
    setExternalDraft((current) => ({ ...(current ?? externalForm), ...values }))
  }
  const updateSystemForm = (values: Partial<AdminOrganizationSyncSettingsPayload>) => {
    setSystemDraft((current) => ({ ...(current ?? systemForm), ...values }))
  }

  useEffect(() => {
    if (externalCompletionMarker === undefined) {
      return
    }
    if (externalCompletionMarkerRef.current === undefined) {
      externalCompletionMarkerRef.current = externalCompletionMarker
      return
    }
    if (
      externalCompletionMarker !== null &&
      externalCompletionMarkerRef.current !== externalCompletionMarker
    ) {
      void queryClient.invalidateQueries({ queryKey: organizationSyncTargetsQueryKey })
    }
    externalCompletionMarkerRef.current = externalCompletionMarker
  }, [externalCompletionMarker, queryClient])

  useEffect(() => {
    if (!externalRunWatch) {
      return
    }

    const timeoutId = window.setTimeout(
      () => {
        setExternalRunWatch(null)
        showFeedback({
          message: t('syncSettings.feedback.externalResultTimeout'),
          tone: 'warning',
        })
      },
      Math.max(0, externalRunWatch.deadlineAt - Date.now()),
    )

    return () => window.clearTimeout(timeoutId)
  }, [externalRunWatch, showFeedback, t])

  useEffect(() => {
    if (!externalRunWatch || !authzConfigQuery.data) {
      return
    }

    const { last_sync_status: status, last_sync_time: lastSyncTime } = authzConfigQuery.data
    if (status === 'running') {
      if (
        lastSyncTime !== null &&
        (externalRunWatch.baselineTime === null || lastSyncTime !== externalRunWatch.baselineTime)
      ) {
        externalRunSawRunningRef.current = true
      }
      return
    }

    const hasNewTimestamp = lastSyncTime !== null && lastSyncTime !== externalRunWatch.baselineTime
    if (!externalRunSawRunningRef.current && !hasNewTimestamp) {
      return
    }

    if (status !== 'success' && status !== 'failed') {
      return
    }

    const terminalFeedbackId = window.setTimeout(() => {
      setExternalRunWatch(null)
      void queryClient.invalidateQueries({ queryKey: organizationSyncTargetsQueryKey })
      showFeedback({
        detail: authzConfigQuery.data.last_sync_message || undefined,
        message: t(
          status === 'success'
            ? 'syncSettings.feedback.externalCompleted'
            : 'syncSettings.feedback.externalResultFailed',
        ),
        tone: status === 'success' ? 'success' : 'error',
      })
    }, 0)

    return () => window.clearTimeout(terminalFeedbackId)
  }, [authzConfigQuery.data, externalRunWatch, queryClient, showFeedback, t])

  return (
    <AdminShellPage activePath="/admin/organization/sync">
      <section className="flex h-full min-w-0 flex-col overflow-auto">
        <PageHeader
          contentClassName="max-w-5xl"
          description={t('syncSettings.description')}
          title={t('syncSettings.title')}
        />
        <SyncSettingsTabs activeTab={activeTab} onTabChange={setActiveTab} t={t} />

        <div className="min-w-0 px-5 pb-6 lg:px-[30px]">
          {activeTab === 'external' ? (
            <PageContentBoundary
              aria-labelledby="sync-settings-external-tab"
              className="grid max-w-5xl gap-4"
              id="sync-settings-external-panel"
              role="tabpanel"
            >
              {authzConfigQuery.isError ? (
                <SyncLoadError
                  description={t('syncSettings.external.loadErrorDescription')}
                  onRetry={() => void authzConfigQuery.refetch()}
                  t={t}
                />
              ) : (
                <SyncCard
                  eyebrow={t('syncSettings.external.pipeline')}
                  title={t('syncSettings.external.settingsTitle')}
                  tooltip={t('syncSettings.external.settingsTooltip')}
                >
                  {needsExternalInitialization ? (
                    <div
                      className="rounded-lg border border-[#efcf87] bg-[#fff8e6] px-4 py-3 text-[#7a4b00]"
                      role="alert"
                    >
                      <p className="text-sm font-semibold">
                        {t('syncSettings.external.uninitializedTitle')}
                      </p>
                      <p className="mt-1 text-sm leading-5">
                        {t('syncSettings.external.uninitializedNotice')}
                      </p>
                    </div>
                  ) : null}
                  <FormField htmlFor="authz-sync-enabled" label={t('syncSettings.fields.enabled')}>
                    <div className="flex h-[38px] items-center">
                      <Switch
                        checked={externalForm.is_enabled}
                        disabled={!canWrite || authzConfigQuery.isLoading}
                        id="authz-sync-enabled"
                        onCheckedChange={(checked) => updateExternalForm({ is_enabled: checked })}
                      />
                    </div>
                  </FormField>
                  <FormField
                    error={!externalCronIsValid ? t('syncSettings.external.cronError') : undefined}
                    hint={t('syncSettings.external.cronHint')}
                    htmlFor="authz-sync-cron"
                    label={t('syncSettings.fields.cron')}
                    labelExtra={
                      <HelpTooltip
                        label={t('syncSettings.external.cronTooltip')}
                        widthClassName="w-[280px]"
                      >
                        {t('syncSettings.external.cronTooltip')}
                      </HelpTooltip>
                    }
                  >
                    <Input
                      disabled={!canWrite || authzConfigQuery.isLoading}
                      id="authz-sync-cron"
                      onChange={(event) =>
                        updateExternalForm({ cron_expression: event.target.value })
                      }
                      value={externalForm.cron_expression}
                    />
                  </FormField>
                  <CardActions>
                    <Button
                      disabled={
                        !canWrite ||
                        (!externalDirty && !needsExternalInitialization) ||
                        !externalCronIsValid
                      }
                      isLoading={saveExternalMutation.isPending}
                      onClick={() => saveExternalMutation.mutate()}
                      size="sm"
                    >
                      {t('syncSettings.actions.saveSettings')}
                    </Button>
                  </CardActions>
                </SyncCard>
              )}

              <SyncCard
                action={
                  <Button
                    disabled={!canWrite || externalRunWatch !== null}
                    isLoading={runExternalMutation.isPending || externalRunWatch !== null}
                    onClick={() => runExternalMutation.mutate()}
                    size="sm"
                    variant="soft"
                  >
                    <SyncActionIcon />
                    {externalRunWatch
                      ? t('syncSettings.actions.pullingExternal')
                      : t('syncSettings.actions.pullExternalNow')}
                  </Button>
                }
                eyebrow={t('syncSettings.external.manualEyebrow')}
                title={t('syncSettings.external.manualTitle')}
                tooltip={t('syncSettings.external.manualTooltip')}
                variant="action"
              >
                <RunSummary
                  completedAt={authzConfigQuery.data?.last_sync_time}
                  message={authzConfigQuery.data?.last_sync_message}
                  status={authzConfigQuery.data?.last_sync_status}
                  t={t}
                />
                <ExternalResultOverview result={authzConfigQuery.data?.last_sync_result} t={t} />
              </SyncCard>
            </PageContentBoundary>
          ) : null}

          {activeTab === 'system' ? (
            <PageContentBoundary
              aria-labelledby="sync-settings-system-tab"
              className="grid max-w-5xl gap-4"
              id="sync-settings-system-panel"
              role="tabpanel"
            >
              {systemSettingsQuery.isError ? (
                <SyncLoadError
                  description={t('syncSettings.system.loadErrorDescription')}
                  onRetry={() => void systemSettingsQuery.refetch()}
                  t={t}
                />
              ) : (
                <>
                  <SyncCard
                    eyebrow={t('syncSettings.system.pipeline')}
                    title={t('syncSettings.system.settingsTitle')}
                    tooltip={t('syncSettings.system.settingsTooltip')}
                  >
                    <FormField
                      htmlFor="system-sync-enabled"
                      label={t('syncSettings.fields.enabled')}
                    >
                      <div className="flex h-[38px] items-center">
                        <Switch
                          checked={systemForm.is_enabled}
                          disabled={!canWrite || systemSettingsQuery.isLoading}
                          id="system-sync-enabled"
                          onCheckedChange={(checked) => updateSystemForm({ is_enabled: checked })}
                        />
                      </div>
                    </FormField>
                    <FormField
                      error={!systemCronIsValid ? t('syncSettings.system.cronError') : undefined}
                      hint={t('syncSettings.system.cronHint')}
                      htmlFor="system-sync-cron"
                      label={t('syncSettings.fields.cron')}
                      labelExtra={
                        <HelpTooltip
                          label={t('syncSettings.system.cronTooltip')}
                          widthClassName="w-[280px]"
                        >
                          {t('syncSettings.system.cronTooltip')}
                        </HelpTooltip>
                      }
                    >
                      <Input
                        disabled={!canWrite || systemSettingsQuery.isLoading}
                        id="system-sync-cron"
                        onChange={(event) =>
                          updateSystemForm({ cron_expression: event.target.value })
                        }
                        value={systemForm.cron_expression}
                      />
                    </FormField>
                    <FormField htmlFor="system-sync-scope" label={t('syncSettings.system.scope')}>
                      <Select
                        aria-label={t('syncSettings.system.scope')}
                        disabled={!canWrite || systemSettingsQuery.isLoading}
                        id="system-sync-scope"
                        onValueChange={(value) =>
                          updateSystemForm({
                            sync_company_ids: value === 'all' ? [] : systemForm.sync_company_ids,
                            sync_scope: value === 'selected' ? 'selected' : 'all',
                          })
                        }
                        options={[
                          {
                            label: t('syncSettings.system.scopeOptions.all'),
                            value: 'all',
                          },
                          {
                            label: t('syncSettings.system.scopeOptions.selected'),
                            value: 'selected',
                          },
                        ]}
                        value={systemForm.sync_scope}
                      />
                    </FormField>
                    {systemForm.sync_scope === 'selected' ? (
                      <FormField
                        error={
                          !systemScopeIsValid
                            ? t('syncSettings.system.targetsRequired')
                            : targetsQuery.isError
                              ? t('syncSettings.system.targetsLoadError')
                              : undefined
                        }
                        label={t('syncSettings.system.targets')}
                      >
                        <div className="grid gap-2 rounded-lg border border-[var(--color-panel-border)] p-3 sm:grid-cols-2">
                          {(targetsQuery.data ?? []).map((target) => (
                            <label
                              className="flex min-h-9 cursor-pointer items-center gap-2 rounded-md px-2 text-sm hover:bg-[var(--color-hover-muted)]"
                              key={target.id}
                            >
                              <input
                                checked={systemForm.sync_company_ids.includes(target.id)}
                                className="size-4 accent-[var(--color-primary)]"
                                disabled={!canWrite}
                                onChange={() =>
                                  updateSystemForm({
                                    sync_company_ids: toggleValue(
                                      systemForm.sync_company_ids,
                                      target.id,
                                    ),
                                  })
                                }
                                type="checkbox"
                              />
                              <span className="min-w-0 truncate">{target.name}</span>
                            </label>
                          ))}
                          {!targetsQuery.isLoading &&
                          !targetsQuery.isError &&
                          (targetsQuery.data ?? []).length === 0 ? (
                            <p className="text-sm text-[var(--color-text-muted)] sm:col-span-2">
                              {t('syncSettings.system.targetsEmpty')}
                            </p>
                          ) : null}
                        </div>
                      </FormField>
                    ) : null}
                    <CardActions>
                      <Button
                        disabled={!canWrite || !systemDirty || !systemFormIsValid}
                        isLoading={saveSystemMutation.isPending}
                        onClick={() => saveSystemMutation.mutate()}
                        size="sm"
                      >
                        {t('syncSettings.actions.saveSettings')}
                      </Button>
                    </CardActions>
                  </SyncCard>

                  <SyncCard
                    action={
                      <Button
                        disabled={
                          !canWrite ||
                          !systemSettingsQuery.data ||
                          systemDirty ||
                          !systemFormIsValid ||
                          systemStatus.is_syncing
                        }
                        isLoading={runSystemMutation.isPending}
                        onClick={() => runSystemMutation.mutate()}
                        size="sm"
                        variant="soft"
                      >
                        <SyncActionIcon />
                        {t('syncSettings.actions.syncToHubNow')}
                      </Button>
                    }
                    eyebrow={t('syncSettings.system.manualEyebrow')}
                    title={t('syncSettings.system.manualTitle')}
                    tooltip={t('syncSettings.system.manualTooltip')}
                    variant="action"
                  >
                    <RunSummary
                      completedAt={systemSettingsQuery.data?.last_run?.completed_at}
                      message={systemSettingsQuery.data?.last_run?.message}
                      status={
                        systemStatus.is_syncing
                          ? 'running'
                          : systemSettingsQuery.data?.last_run?.status
                      }
                      t={t}
                    />
                    <SystemResultOverview result={systemSettingsQuery.data?.last_run} t={t} />
                  </SyncCard>
                </>
              )}
            </PageContentBoundary>
          ) : null}

          {activeTab === 'history' ? (
            <div
              aria-labelledby="sync-settings-history-tab"
              className="grid gap-5"
              id="sync-settings-history-panel"
              role="tabpanel"
            >
              <SyncCard
                eyebrow={t('syncSettings.external.pipeline')}
                title={t('syncSettings.history.externalLatestTitle')}
                tooltip={t('syncSettings.history.externalLatestTooltip')}
              >
                {authzConfigQuery.isError ? (
                  <SyncLoadError
                    description={t('syncSettings.external.loadErrorDescription')}
                    onRetry={() => void authzConfigQuery.refetch()}
                    t={t}
                  />
                ) : (
                  <>
                    <RunSummary
                      completedAt={authzConfigQuery.data?.last_sync_time}
                      message={authzConfigQuery.data?.last_sync_message}
                      status={authzConfigQuery.data?.last_sync_status}
                      t={t}
                    />
                    <ExternalResultOverview
                      result={authzConfigQuery.data?.last_sync_result}
                      t={t}
                    />
                  </>
                )}
              </SyncCard>

              <section className="grid gap-3">
                <SyncSectionHeading
                  title={t('syncSettings.history.systemRunsTitle')}
                  tooltip={t('syncSettings.history.systemRunsTooltip')}
                />
                {runsQuery.isError ? (
                  <SyncLoadError
                    description={t('syncSettings.history.loadErrorDescription')}
                    onRetry={() => void runsQuery.refetch()}
                    t={t}
                  />
                ) : (
                  <DataTable
                    columns={runColumns}
                    empty={t('syncSettings.history.empty')}
                    getRowKey={(run) => run.id}
                    isLoading={runsQuery.isLoading}
                    minWidth={1160}
                    rows={runsQuery.data ?? []}
                  />
                )}
              </section>
            </div>
          ) : null}
        </div>
      </section>
    </AdminShellPage>
  )
}

const initialSystemLock = {
  company_id: null,
  company_ids: [],
  is_syncing: false,
  source: null,
  started_at: null,
}

function SyncSettingsTabs({
  activeTab,
  onTabChange,
  t,
}: {
  activeTab: SyncSettingsTab
  onTabChange: (tab: SyncSettingsTab) => void
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t']
}) {
  const tabs: { id: SyncSettingsTab; label: string }[] = [
    { id: 'external', label: t('syncSettings.tabs.external') },
    { id: 'system', label: t('syncSettings.tabs.system') },
    { id: 'history', label: t('syncSettings.tabs.history') },
  ]

  return (
    <div className="relative z-20 shrink-0 bg-white px-5 pb-4 lg:px-[30px]">
      <PageContentBoundary className="max-w-5xl">
        <div
          aria-label={t('syncSettings.tabs.label')}
          className="flex h-12 min-w-0 gap-8 overflow-x-auto border-b border-[var(--color-divider)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          role="tablist"
        >
          {tabs.map((tab) => {
            const selected = activeTab === tab.id
            return (
              <button
                aria-controls={`sync-settings-${tab.id}-panel`}
                aria-selected={selected}
                className={cn(
                  'h-12 shrink-0 cursor-pointer border-b-2 px-1 text-sm font-medium transition-colors',
                  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--color-focus)]',
                  selected
                    ? 'border-[var(--color-primary)] font-semibold text-[var(--color-text)]'
                    : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]',
                )}
                id={`sync-settings-${tab.id}-tab`}
                key={tab.id}
                onClick={() => onTabChange(tab.id)}
                role="tab"
                type="button"
              >
                {tab.label}
              </button>
            )
          })}
        </div>
      </PageContentBoundary>
    </div>
  )
}

function SyncCard({
  action,
  children,
  eyebrow,
  title,
  tooltip,
  variant = 'default',
}: {
  action?: ReactNode
  children: ReactNode
  eyebrow?: string
  title: string
  tooltip?: string
  variant?: 'action' | 'default'
}) {
  return (
    <section
      className={cn(
        'grid content-start gap-5 rounded-xl border p-5 shadow-[var(--shadow-panel-subtle)]',
        variant === 'action'
          ? 'border-[rgba(25,91,255,0.22)] bg-[linear-gradient(180deg,rgba(25,91,255,0.035),white_42%)]'
          : 'border-[var(--color-panel-border)] bg-white',
      )}
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="grid gap-1">
          {eyebrow ? (
            <p className="text-xs font-medium tracking-wide text-[var(--color-primary)]">
              {eyebrow}
            </p>
          ) : null}
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-[var(--color-text)]">{title}</h2>
            {tooltip ? (
              <HelpTooltip label={tooltip} widthClassName="w-[300px]">
                {tooltip}
              </HelpTooltip>
            ) : null}
          </div>
        </div>
        {action ? <div className="shrink-0 self-start">{action}</div> : null}
      </div>
      {children}
    </section>
  )
}

function CardActions({ children }: { children: ReactNode }) {
  return (
    <div className="flex justify-end border-t border-[var(--color-divider)] pt-4">{children}</div>
  )
}

function ExternalResultOverview({
  result,
  t,
}: {
  result?: AdminAuthzSyncResult | null
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t']
}) {
  if (!result) {
    return (
      <ResultOverviewShell t={t}>
        <p className="text-xs leading-5 text-[var(--color-text-muted)]">
          {t('syncSettings.result.emptyExternal')}
        </p>
      </ResultOverviewShell>
    )
  }

  return (
    <ResultOverviewShell t={t}>
      <dl className="flex flex-wrap gap-y-2">
        <ResultSummaryItem
          details={t('syncSettings.result.externalDelta', {
            created: result.accounts.created,
            disabled: result.accounts.disabled,
            updated: result.accounts.updated,
          })}
          label={t('syncSettings.result.accounts')}
          total={result.accounts.total}
        />
        <ResultSummaryItem
          details={t('syncSettings.result.externalDelta', {
            created: result.org_units.created,
            disabled: result.org_units.disabled,
            updated: result.org_units.updated,
          })}
          label={t('syncSettings.result.orgUnits')}
          total={result.org_units.total}
        />
        <ResultSummaryItem
          details={t('syncSettings.result.relationDelta', {
            created: result.relations.created,
            removed: result.relations.removed,
            updated: result.relations.updated,
          })}
          label={t('syncSettings.result.relations')}
          total={result.relations.total}
        />
      </dl>
      {result.warnings_count > 0 ? (
        <p className="text-xs text-[#8a5a00]">
          {t('syncSettings.result.warnings', { count: result.warnings_count })}
        </p>
      ) : null}
    </ResultOverviewShell>
  )
}

function SystemResultOverview({
  result,
  t,
}: {
  result?: AdminOrganizationSyncRun | null
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t']
}) {
  return (
    <ResultOverviewShell t={t}>
      <dl className="flex flex-wrap gap-y-2">
        <ResultSummaryItem
          details={t('syncSettings.result.synced')}
          label={t('syncSettings.result.syncedOrgUnits')}
          total={result?.units_count}
        />
        <ResultSummaryItem
          details={t('syncSettings.result.synced')}
          label={t('syncSettings.result.syncedRelations')}
          total={result?.relations_count}
        />
        <ResultSummaryItem
          details={t('syncSettings.result.skippedDetail', {
            relations: result?.skipped_relations ?? 0,
            units: result?.skipped_units ?? 0,
          })}
          label={t('syncSettings.result.skipped')}
          total={result ? result.skipped_units + result.skipped_relations : undefined}
        />
      </dl>
    </ResultOverviewShell>
  )
}

function ResultOverviewShell({
  children,
  t,
}: {
  children: ReactNode
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t']
}) {
  return (
    <section
      aria-label={t('syncSettings.result.title')}
      className="grid gap-2 border-t border-[var(--color-divider)] pt-4"
    >
      <div className="flex items-center gap-2">
        <h3 className="text-xs font-medium text-[var(--color-text-muted)]">
          {t('syncSettings.result.title')}
        </h3>
        <HelpTooltip label={t('syncSettings.result.tooltip')} widthClassName="w-[320px]">
          {t('syncSettings.result.tooltip')}
        </HelpTooltip>
      </div>
      {children}
    </section>
  )
}

function ResultSummaryItem({
  details,
  label,
  total,
}: {
  details: string
  label: string
  total?: number
}) {
  return (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 border-l border-[var(--color-divider)] px-4 text-xs first:border-l-0 first:pl-0">
      <dt className="text-[var(--color-text-subtle)]">{label}</dt>
      <dd className="font-semibold tabular-nums text-[var(--color-text-muted)]">
        {typeof total === 'number' ? total.toLocaleString() : '-'}
      </dd>
      <dd className="text-[var(--color-text-muted)]">({details})</dd>
    </div>
  )
}

function SyncActionIcon() {
  return (
    <svg aria-hidden className="size-4 shrink-0" fill="none" viewBox="0 0 20 20">
      <path
        d="M3.5 6.25h11m0 0L12 3.75m2.5 2.5L12 8.75m4.5 5H5.5m0 0L8 11.25m-2.5 2.5L8 16.25"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
      />
    </svg>
  )
}

function RunSummary({
  completedAt,
  message,
  status,
  t,
}: {
  completedAt?: string | null
  message?: string | null
  status?: string
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t']
}) {
  return (
    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(140px,0.7fr)_minmax(220px,1fr)_minmax(0,2fr)]">
      <RunSummaryField label={t('syncSettings.status.label')}>
        <Badge variant={statusVariant(status)}>{statusLabel(status, t)}</Badge>
      </RunSummaryField>
      <RunSummaryField label={t('syncSettings.status.lastSyncTime')}>
        {formatDateTime(completedAt)}
      </RunSummaryField>
      <RunSummaryField
        className="sm:col-span-2 lg:col-span-1"
        label={t('syncSettings.status.message')}
      >
        {displayText(message)}
      </RunSummaryField>
    </dl>
  )
}

function RunSummaryField({
  children,
  className,
  label,
}: {
  children: ReactNode
  className?: string
  label: string
}) {
  return (
    <div className={cn('min-w-0 rounded-lg bg-[var(--color-surface-subtle)] px-4 py-3', className)}>
      <dt className="text-xs font-medium text-[var(--color-text-muted)]">{label}</dt>
      <dd className="mt-2 break-words text-sm leading-5 text-[var(--color-text)]">{children}</dd>
    </div>
  )
}

function SyncLoadError({
  description,
  onRetry,
  t,
}: {
  description: string
  onRetry: () => void
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t']
}) {
  return (
    <div className="flex flex-col items-start gap-3 rounded-xl border border-dashed border-[var(--color-panel-border)] bg-[var(--color-surface-subtle)] px-5 py-4">
      <div>
        <p className="text-sm font-semibold text-[var(--color-text)]">
          {t('syncSettings.loadError')}
        </p>
        <p className="mt-1 text-sm leading-5 text-[var(--color-text-muted)]">{description}</p>
      </div>
      <Button onClick={onRetry} size="sm" variant="secondary">
        {t('actions.retry')}
      </Button>
    </div>
  )
}

function SyncSectionHeading({ title, tooltip }: { title: string; tooltip: string }) {
  return (
    <div className="flex items-center gap-2">
      <h2 className="text-base font-semibold text-[var(--color-text)]">{title}</h2>
      <HelpTooltip label={tooltip} widthClassName="w-[300px]">
        {tooltip}
      </HelpTooltip>
    </div>
  )
}

function hasCronFields(value: string, count: number) {
  return value.trim().split(/\s+/).length === count
}

function toggleValue(values: string[], value: string) {
  return values.includes(value) ? values.filter((item) => item !== value) : [...values, value]
}

function externalRunErrorMessage(
  error: unknown,
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t'],
) {
  if (error instanceof ApiError && error.errorCode === 'authz.sync_not_initialized') {
    return t('syncSettings.feedback.externalNotInitialized')
  }
  return t('syncSettings.feedback.externalRunFailed')
}

function statusVariant(status?: string) {
  if (status === 'success') {
    return 'success' as const
  }
  if (status === 'running') {
    return 'warning' as const
  }
  if (status === 'failed') {
    return 'danger' as const
  }
  return 'neutral' as const
}

function displayText(value?: string | null) {
  if (!value?.trim()) {
    return '-'
  }
  return value
}

function statusLabel(
  status: string | undefined,
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t'],
) {
  if (
    status === 'success' ||
    status === 'running' ||
    status === 'failed' ||
    status === 'abandoned'
  ) {
    return t(`syncSettings.status.values.${status}`)
  }
  return t('syncSettings.status.values.unknown')
}

function formatDuration(durationMs: number | null) {
  if (durationMs === null) {
    return '-'
  }
  if (durationMs < 1_000) {
    return `${String(durationMs)} ms`
  }
  return `${(durationMs / 1_000).toFixed(durationMs < 10_000 ? 1 : 0)} s`
}

function formatRunScope(
  run: AdminOrganizationSyncRun,
  targetNameById: Map<string, string>,
  t: ReturnType<typeof useTranslation<'adminOrganization'>>['t'],
) {
  const companyIds =
    run.company_ids.length > 0 ? run.company_ids : run.company_id ? [run.company_id] : []
  if (companyIds.length === 0) {
    return t('syncSettings.system.scopeOptions.all')
  }
  return companyIds.map((id) => targetNameById.get(id) ?? id).join(' / ')
}
